import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import { handleEmailVerification } from '../../services/authService';

const EmailActionHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const mode = searchParams.get('mode');
    const actionCode = searchParams.get('oobCode');

    if (!mode || !actionCode) {
      setError('Invalid or missing parameters.');
      setLoading(false);
      return;
    }

    handleAction(mode, actionCode);
  }, [searchParams]);

  const handleAction = async (mode, actionCode) => {
    try {
      switch (mode) {
        case 'verifyEmail':
          await handleEmailVerification(actionCode);
          setSuccess(true);
          break;
        case 'resetPassword':
          navigate(`/reset-password?oobCode=${actionCode}`);
          return;
        default:
          setError('Unknown action mode.');
      }
    } catch (error) {
      console.error(error);
      if (error.code === 'auth/invalid-action-code') {
        setError('This verification link is invalid or has expired.');
      } else if (error.code === 'auth/expired-action-code') {
        setError('This verification link has expired. Please request a new one.');
      } else {
        setError('An error occurred while processing your request.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 4
          }}
        >
          <Paper
            elevation={6}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <CircularProgress size={50} sx={{ mb: 2 }} />
            <Typography variant="h6">
              Processing your request...
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 4
          }}
        >
          <Paper
            elevation={6}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" color="error" gutterBottom>
              Verification Failed
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/register')}
              >
                Create New Account
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '30vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 4
          }}
        >
          <Paper
            elevation={6}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" color="success.main" gutterBottom>
              Email Verified!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your email address has been successfully verified. You can now sign in to your account.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
            >
              Continue to Sign In
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return null;
};

export default EmailActionHandler;